import { quickTimer } from "./quick-timer";
import lamejs from "lamejs";
import { LiveClient } from "@deepgram/sdk";

export class MicRecorder {
  mediaStream: MediaStream = null;
  mediaRecorder: MediaRecorder = null;
  mp3Encoder: lamejs.Encoder = null;

  setSoundDetected: (soundDetected: boolean) => void = null;
  setVolume: (volume: number) => void = null;
  analyserNode: AnalyserNode = null;

  isPaused = false;

  socket: LiveClient = null;

  audioContext: AudioContext;
  sourceAudioNode: AudioNode;

  partialBuffer: Blob[] = [];
  finalBuffer: Blob[] = [];

  startOscillator = () => {
    const oscillatorData = new Uint8Array(this.analyserNode.frequencyBinCount);
    const pcmData = new Float32Array(this.analyserNode.fftSize);

    this.setSoundDetected(true);
    let soundDetectedTimeout = setTimeout(
      () => this.setSoundDetected(false),
      5000
    );

    let smoothVolume = 0; // Tracks smoothed volume levels

    const onFrame = () => {
      if (!this.analyserNode) {
        return;
      }

      this.analyserNode.getByteFrequencyData(oscillatorData);
      this.analyserNode.getFloatTimeDomainData(pcmData);

      let instantVolume =
        oscillatorData.reduce((sum, value) => sum + value, 0) /
        oscillatorData.length;
      smoothVolume = smoothVolume * 0.8 + instantVolume * 0.2;
      this.setVolume(smoothVolume);

      if (instantVolume > 20) {
        if (soundDetectedTimeout) {
          clearTimeout(soundDetectedTimeout);
          soundDetectedTimeout = null;
        }

        this.setSoundDetected(true);
      } else {
        if (!soundDetectedTimeout) {
          soundDetectedTimeout = setTimeout(
            () => this.setSoundDetected(false),
            5000
          );
        }
      }

      window.requestAnimationFrame(onFrame);
    };

    window.requestAnimationFrame(onFrame);
  };

  initializeMediaStream = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      alert(
        "Recording only works with the https protocol. Please check the URL and try again."
      );

      throw new Error("Recording only works with the https protocol");
    }

    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        sampleRate: 16000
      }
    });

    this.audioContext = new AudioContext();
    this.mp3Encoder = new lamejs.Mp3Encoder(1, 16000, 128);
    this.partialBuffer = [];

    this.analyserNode = this.audioContext.createAnalyser();
    this.analyserNode.fftSize = 128;

    this.sourceAudioNode = this.audioContext.createMediaStreamSource(
      this.mediaStream
    );

    this.sourceAudioNode.connect(this.analyserNode);

    this.startOscillator();

    this.mediaRecorder = new MediaRecorder(this.mediaStream, {
      audioBitsPerSecond: 128000
    });

    this.mediaRecorder.addEventListener("dataavailable", (event) => {
      if (event.data.size > 0) {
        this.partialBuffer.push(event.data);
        this.finalBuffer.push(event.data);

        if (this.socket && this.socket.getReadyState() === 1) {
          this.socket.send(event.data);
        }
      }
    });

    if (this.socket) {
      this.mediaRecorder.start(100);
    } else {
      this.mediaRecorder.start(500);
    }

    const audioTrack = this.mediaStream.getAudioTracks()[0];

    return {
      label: audioTrack.label,
      deviceId:
        audioTrack.getCapabilities && audioTrack.getCapabilities().deviceId
    };
  };

  start = async (
    socket: LiveClient,
    setSoundDetected: (soundDetected: boolean) => void = null,
    setVolume: (volume: number) => void = null
  ) => {
    this.setSoundDetected = setSoundDetected;
    this.setVolume = setVolume;
    this.socket = socket;

    this.finalBuffer = [];
    return await this.initializeMediaStream();
  };

  resume = async (socket: LiveClient) => {
    this.socket = socket;
    this.isPaused = false;

    return await this.initializeMediaStream();
  };

  pause = async () => {
    if (this.isPaused == false) {
      this.isPaused = true;

      await quickTimer(1500);
      await this.stop();
    }
  };

  stop = async () => {
    this.isPaused = false;

    this.mediaStream?.getAudioTracks()?.forEach((track) => track.stop());
    this.mediaStream = null;

    this.mediaRecorder?.stop();
    this.mediaRecorder = null;

    this.analyserNode?.disconnect();
    this.sourceAudioNode?.disconnect();
  };

  downSample = (
    buffer: Float32Array,
    inputSampleRate: number,
    outputSampleRate: number
  ) => {
    if (outputSampleRate === inputSampleRate) {
      return new Float32Array(buffer);
    }

    const sampleRateRatio = inputSampleRate / outputSampleRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;

    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);

      let accum = 0,
        count = 0;

      for (
        let i = offsetBuffer;
        i < nextOffsetBuffer && i < buffer.length;
        i++
      ) {
        accum += buffer[i];
        count++;
      }

      result[offsetResult] = accum / count;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }

    return result;
  };

  convertFloat32To16BitPCM = (input: Float32Array) => {
    const output = new Int16Array(input.length);

    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]));
      // output[i] = s < 0 ? s * 32768 : s * 32767
      // output[i] = s * 32400
      output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }

    return output;
  };

  getFile = async (partial: boolean = false) => {
    const buffer = partial ? this.partialBuffer : this.finalBuffer;

    const blob = new Blob(buffer);

    const randomFileName = Date.now();
    const file = new File([blob], randomFileName.toString() + ".mp3");

    return file;
  };
}
